import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getEditorVersionFilter, getZyVersions, normalizeSelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import { Spinner } from '@radix-ui/themes/components/spinner';
import { Editor } from '@tiptap/react';
import React, { useState } from 'react';

export type ZyPreviewModeToggleOptions = { previewVersion?: string; snapToGuid?: string };

export type ZyPreviewButtonProps = {
    editor: Editor | null;
    previewMode: boolean;
    disabled?: boolean;
    onTogglePreview: (options?: ZyPreviewModeToggleOptions) => void | Promise<void>;
};

export const ZyPreviewButton: React.FC<ZyPreviewButtonProps> = ({ editor, previewMode, disabled = false, onTogglePreview }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [versionChoices, setVersionChoices] = useState<string[]>([]);
    const [pendingVersion, setPendingVersion] = useState<string>('');

    const handleClick = async () => {
        if (previewMode) {
            await onTogglePreview();
            return;
        }
        if (!editor) {
            return;
        }
        const allVersions = getZyVersions(editor);
        if (allVersions.length === 0) {
            await onTogglePreview();
            return;
        }

        const normalized = normalizeSelectedZyVersionFilter(getEditorVersionFilter(editor));
        if (normalized !== null && normalized.length === 0) {
            await onTogglePreview();
            return;
        }
        if (normalized !== null && normalized.length === 1) {
            await onTogglePreview({ previewVersion: normalized[0] });
            return;
        }
        const choices = normalized ?? allVersions;
        setVersionChoices(choices);
        setPendingVersion(choices[0] ?? '');
        setDialogOpen(true);
    };

    const confirmVersion = async () => {
        setDialogOpen(false);
        await onTogglePreview({ previewVersion: pendingVersion });
    };

    return (
        <>
            <Button
                size="sm"
                disabled={disabled}
                onClick={() => void handleClick()}
                id="zypreview-button"
            >
                {disabled && <Spinner className="mr-2" />} {previewMode ? 'Edit' : 'Preview'}
            </Button>

            <Dialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
            >
                <DialogContent className="max-w-md sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Choose preview version</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-gray-600">Multiple versions are selected, pick one version to preview.</p>
                    <div className="py-2">
                        <label className="mb-1 block text-sm text-gray-600">Version</label>
                        <Select
                            value={pendingVersion}
                            onValueChange={setPendingVersion}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Version" />
                            </SelectTrigger>
                            <SelectContent>
                                {versionChoices.map(v => (
                                    <SelectItem
                                        key={v}
                                        value={v}
                                    >
                                        {v}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => void confirmVersion()}
                        >
                            Preview
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
